import { type ReactNode, useEffect } from "react";
import { Navigate, Outlet, useLocation } from "react-router";

import { useSession } from "@/api/hooks/auth";
import type { Role } from "@/api/types";
import { FullPageSkeleton } from "@/components/common/query-state";
import { safeNext } from "@/lib/navigation";
import { goToSso, rememberSignedOut, signedOutHere } from "@/lib/sso";

import { TermsDialog } from "./terms-dialog";

/**
 * Renders children (or <Outlet/>) only for signed-in users.
 *
 * A visitor without a session goes straight to Hume ID (contract §10, §13 row 2), silently first
 * (`prompt=none`): people signed in to another Hume app come straight back to this page; when Hume ID needs
 * them, the server tries again interactively. The sign-in page (/login?next=…) shows only after they signed out
 * in this tab (`signedOutHere`) or while the deployment isn't connected to Hume ID.
 * Accounts that haven't accepted the current Terms see the blocking consent dialog first.
 */
export function RequireAuth({ children }: { children?: ReactNode }) {
  const { data: session, isPending } = useSession();
  const location = useLocation();
  const here = location.pathname + location.search;
  const silent = !isPending && !session?.authenticated && Boolean(session?.tenant.connected) && !signedOutHere();
  const authenticated = Boolean(session?.authenticated);
  useEffect(() => {
    if (silent) goToSso(here, { prompt: "none" });
    if (authenticated) rememberSignedOut(false); // signed in again (e.g. through Hume ID)
  }, [silent, here, authenticated]);
  if (isPending || silent) return <FullPageSkeleton />;
  if (!session?.authenticated) {
    return <Navigate to={`/login?next=${encodeURIComponent(here)}`} replace />;
  }
  if (session.user?.terms_required) {
    // The API refuses everything else until then, so the app waits behind the dialog.
    return (
      <>
        <FullPageSkeleton />
        <TermsDialog />
      </>
    );
  }
  return <>{children ?? <Outlet />}</>;
}

/** Restricts a route to one role; other roles are sent to their home. */
export function RequireRole({ role, children }: { role: Role; children?: ReactNode }) {
  const { data: session } = useSession();
  if (session?.user?.role !== role) return <Navigate to="/app" replace />;
  return <>{children ?? <Outlet />}</>;
}

/** For the sign-in page: signed-in users go straight to the app (or ?next=). */
export function RedirectIfAuthenticated({ children }: { children?: ReactNode }) {
  const { data: session, isPending } = useSession();
  const location = useLocation();
  if (isPending) return <FullPageSkeleton />;
  if (session?.authenticated) {
    const next = new URLSearchParams(location.search).get("next");
    return <Navigate to={safeNext(next)} replace />;
  }
  return <>{children ?? <Outlet />}</>;
}

/** "/" is "/login", as on the server (core.views.spa): one sign-in page, which a signed-in person skips. */
export function RootToLogin() {
  const { search } = useLocation();
  return <Navigate to={`/login${search}`} replace />;
}
